/**
 * ai-context.service.ts
 *
 * Typed API call wrappers for the AI contextual sidebar.
 * Types match the backend ai-context.service.ts response shapes exactly.
 */
import { apiClient } from './apiClient';

// ─── Response Types (match backend exactly) ─────────────────────────────────

export interface PatientContext {
  patientId: number;
  name: string;
  aiSummary: string;
  riskFlags: string[];
  suggestions: string[];
  demographics: {
    age: number | null;
    gender: string | null;
    dateOfBirth: string | null;
    email: string | null;
    phone: string | null;
  };
  visitSummary: {
    totalVisits: number;
    lastVisitDate: string | null;
    noShowCount: number;
    cancellationCount: number;
  };
  spendingSummary: {
    totalSpent: number;
    averagePerVisit: number;
    lastPaymentDate: string | null;
    outstandingBalance: number;
  };
  activePlans: Array<{
    planId: number;
    stage: string | null;
  }>;
  recentNotes: Array<{
    message: string;
    date: string;
    author: string;
  }>;
}

export interface AppointmentContext {
  appointmentId: number;
  aiSummary: string;
  suggestions: string[];
  appointmentType: string | null;
  scheduledTime: string | null;
  patientSummary: {
    name: string;
    totalVisits: number;
    noShowCount: number;
    totalSpent: number;
  };
  noShowHistory: {
    count: number;
    lastNoShow: string | null;
  };
}

export interface LeadContext {
  leadId: number;
  aiSummary: string;
  suggestions: string[];
  name: string;
  status: string | null;
  source: string | null;
  daysSinceCreated: number;
  communicationCount: number;
}

export interface PlanContext {
  planId: number;
  aiSummary: string;
  suggestions: string[];
  patientName: string;
  procedures: string[];
  stageName: string | null;
  stage: string | null;
  paymentStatus: {
    totalQuoted: number;
    totalPaid: number;
    balance: number;
  };
}

export type ContextType = 'patient' | 'appointment' | 'lead' | 'plan';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  contextType: ContextType;
  contextId: number;
  question: string;
  history: ChatMessage[];
}

export interface PageContextInfo {
  route: string;
  pageName: string;
  filters?: string;
  dataSummary?: string;
  extra?: string;
}

export interface PageChatRequest {
  question: string;
  pageContext: PageContextInfo;
  entityContext?: { contextType: ContextType; contextId: number };
  history: ChatMessage[];
}

// ─── API Functions ───────────────────────────────────────────────────────────

export function getPatientContext(patientId: number): Promise<PatientContext> {
  return apiClient
    .get<{ success: boolean; data: PatientContext }>(`/ai-context/patient/${patientId}`)
    .then((r) => r.data.data);
}

export function getAppointmentContext(appointmentId: number): Promise<AppointmentContext> {
  return apiClient
    .get<{ success: boolean; data: AppointmentContext }>(`/ai-context/appointment/${appointmentId}`)
    .then((r) => r.data.data);
}

export function getLeadContext(leadId: number): Promise<LeadContext> {
  return apiClient
    .get<{ success: boolean; data: LeadContext }>(`/ai-context/lead/${leadId}`)
    .then((r) => r.data.data);
}

export function getPlanContext(planId: number): Promise<PlanContext> {
  return apiClient
    .get<{ success: boolean; data: PlanContext }>(`/ai-context/plan/${planId}`)
    .then((r) => r.data.data);
}

export function chatWithContext(request: ChatRequest): Promise<string> {
  return apiClient
    .post<{ data: { answer: string } }>('/ai-context/chat', request)
    .then((r) => r.data.data.answer);
}

export function chatWithPageContext(request: PageChatRequest): Promise<string> {
  return apiClient
    .post<{ data: { answer: string } }>('/ai-context/page-chat', request)
    .then((r) => r.data.data.answer);
}
